"use client";

/**
 * Export dropdown — the only way content leaves the tab. Every action
 * marks the session exported (clearing the unload warning) on success.
 */
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { markExported } from "@/lib/client/session-store";
import { toast } from "@/lib/toast";
import { logError } from "@/lib/logging";

export interface ExportAction {
  label: string;
  run: () => void | Promise<void>;
  /** Toast to show on success (e.g. "Copied"). Downloads speak for themselves. */
  successMessage?: string;
}

interface ExportMenuProps {
  actions: ExportAction[];
  disabled?: boolean;
  /** Optional output option (e.g. transcript timestamps) shown above the formats. */
  toggle?: {
    label: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
  };
}

export function ExportMenu({ actions, disabled, toggle }: ExportMenuProps) {
  const t = useTranslations("Flow.export");
  async function handleAction(action: ExportAction) {
    try {
      await action.run();
      markExported();
      if (action.successMessage) toast.success(action.successMessage);
    } catch (error) {
      logError(error, "[flow] Export failed", { action: action.label });
      toast.error(t("failed", { label: action.label }));
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex h-[30px] items-center gap-1.5 rounded-lg border border-input px-3 text-[12.5px] font-medium transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-45"
        >
          {t("button")}
          <ChevronDown className="h-[11px] w-[11px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[190px] rounded-[10px] p-[5px] shadow-elev"
      >
        {toggle && (
          <>
            <DropdownMenuCheckboxItem
              checked={toggle.checked}
              onCheckedChange={toggle.onCheckedChange}
              // Keep the menu open so the user can toggle, then pick a format.
              onSelect={(event) => event.preventDefault()}
              className="cursor-pointer rounded-md py-[7px] text-[13px]"
            >
              {toggle.label}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
          </>
        )}
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            className="cursor-pointer rounded-md px-2.5 py-[7px] text-[13px]"
            onSelect={() => void handleAction(action)}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
